import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { missionPath, randomSuffix } from '../missionPath';
import { db } from './config';
import type { UserData } from './user/types';
import { syncPublicProfile } from './user/publicProfile';

export type { UserData } from './user/types';
export { getPublicProfile } from './user/publicProfile';

export async function getUserData(userId: string): Promise<UserData | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;
    return userDoc.data() as UserData;
  } catch (error: any) {
    console.error('Error fetching user data:', error);
    throw new Error(error.message || 'Failed to fetch user data');
  }
}

export async function updateUserData(userId: string, data: Partial<UserData>): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const timestamp = serverTimestamp();

    // Get current data first
    const currentDoc = await getDoc(userRef);

    let mergedData: Record<string, any>;
    if (!currentDoc.exists()) {
      // If document doesn't exist, create it
      mergedData = { ...data, createdAt: timestamp, updatedAt: timestamp };
      await setDoc(userRef, mergedData);
    } else {
      // If document exists, update it
      mergedData = { ...currentDoc.data(), ...data, updatedAt: timestamp };
      await updateDoc(userRef, {
        ...data,
        updatedAt: timestamp,
      });
    }

    // Mirror the public-safe fields to publicProfiles/{uid} so cross-user
    // reads (post authors, mission pages, dashboards) no longer need to read
    // the full /users/{uid} doc, which also carries email/Stripe fields
    // (DF-185/DF-190). Best-effort -- must not fail the profile save itself.
    await syncPublicProfile(userId, mergedData);
  } catch (error: any) {
    console.error('Error updating user data:', error);
    throw new Error(error.message || 'Failed to update user data');
  }
}

export { toPathSegment } from '../missionPath';

/**
 * Check whether a given path is available (no other user has it).
 */
export async function isPathAvailable(path: string, excludeUserId?: string): Promise<boolean> {
  // Reads the public `paths/{slug} -> { userId }` mapping instead of running a
  // collection query over /users. Same answer, but it does not require the
  // caller to be able to LIST /users — which is what let any signed-in user
  // enumerate every user document and harvest their email addresses.
  // resolveUserIdFromIdentifier() below already prefers this same mapping, and
  // it is written on every path change in DashboardLayout, so it is the
  // authoritative record of which slugs are taken.
  const { doc, getDoc } = await import('firebase/firestore');
  const snap = await getDoc(doc(db, 'paths', path));
  if (!snap.exists()) return true;
  const owner = (snap.data() as { userId?: string } | undefined)?.userId;
  // A mapping with no userId is malformed; treat the slug as taken rather than
  // handing it out and creating a collision.
  if (excludeUserId && owner === excludeUserId) return true;
  return false;
}

/**
 * The user's mission page address: firstname-lastname-<6 id chars>, e.g.
 * jussi-savolainen-8j24ij. The suffix is stable per uid; if another user
 * already holds that exact path, a random suffix is used instead.
 */
export async function generateUniquePath(firstName?: string, lastName?: string, excludeUserId?: string): Promise<string> {
  if (excludeUserId) {
    const stable = missionPath(firstName, lastName, excludeUserId);
    if (await isPathAvailable(stable, excludeUserId)) return stable;
  }
  for (let i = 0; i < 20; i++) {
    const candidate = missionPath(firstName, lastName, excludeUserId ?? '', randomSuffix());
    if (await isPathAvailable(candidate, excludeUserId)) return candidate;
  }
  return missionPath(firstName, lastName, excludeUserId ?? '', `${Date.now().toString(36).slice(-6)}`);
}

/**
 * Resolve a userId from a given identifier which may be a UID or a path.
 */
export async function resolveUserIdFromIdentifier(identifier: string): Promise<string | null> {
  // Fast path: only if identifier looks like a Firebase UID (typically 28 chars, URL-safe)
  const looksLikeUid = /^[A-Za-z0-9_-]{20,}$/.test(identifier);
  if (looksLikeUid) {
    try {
      // Prefer checking for a mission page document directly; this does not
      // require the presence of a public users/{uid} document.
      const pageSnap = await getDoc(doc(db, 'missionpages', identifier));
      if (pageSnap.exists()) return identifier;
    } catch { /* ignore and try user doc next */ }
    try {
      // DF-190: existence probe only, so use the PII-free public mirror rather
      // than /users/{uid} — which is now owner-only.
      const pub = await getDoc(doc(db, 'publicProfiles', identifier));
      if (pub.exists()) return identifier;
    } catch { /* ignore and fall back to path lookup */ }
  }

  // Otherwise, resolve via public mapping collection `paths/{slug}` -> { userId }
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const mapping = await getDoc(doc(db, 'paths', identifier));
    if (mapping.exists()) {
      const data = mapping.data() as any;
      return data.userId || null;
    }
  } catch (_) { /* ignore */ }

  // No match via UID fast-path or the public `paths/{slug}` mapping.
  // There used to be two further fallbacks here -- scanning `users` by its
  // `path` field, then scanning `missions` by `slug` -- but firestore.rules
  // denies `list` on both collections unconditionally (`allow list: if
  // false`) for every caller, signed-in or not. The `users` query could
  // never succeed and only logged a "Missing or insufficient permissions"
  // console error on every unresolved identifier (TSF-213, ported from
  // sportfund and mystudyfund). The `missions` query was already
  // unreachable dead code before this fix (every branch of the `users` try
  // above returned), which is the only reason firestore.rules could safely
  // close its own `list` to `false` (see the comment at firestore.rules
  // `match /missions/{missionId}`) -- so it must stay unreachable, not be
  // "fixed" into a live call that would just hit the same permission-denied
  // wall one collection later.
  return null;
}
